import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Search, Bookmark, Moon, Zap, ArrowRight } from 'lucide-react';

export function LandingPage() {
    return (
        <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 selection:bg-indigo-100 dark:selection:bg-indigo-900/40">
            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16 items-center">
                        <div className="flex items-center gap-2 font-bold text-indigo-600 dark:text-indigo-400">
                            <BookOpen size={28} />
                            <span className="text-2xl tracking-tight">Eolas</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link
                                to="/app"
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-full font-medium transition-all shadow-lg shadow-indigo-200 dark:shadow-none hover:scale-[1.02] active:scale-[0.98]"
                            >
                                Launch App
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-sm font-medium mb-6 animate-fade-in">
                        <Zap size={14} />
                        <span>Built with Bun + React for ultimate speed</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 bg-gradient-to-r from-slate-900 via-indigo-900 to-indigo-600 dark:from-white dark:via-indigo-200 dark:to-indigo-400 bg-clip-text text-transparent">
                        Research paper discovery, <br className="hidden md:block" /> modernized.
                    </h1>
                    <p className="text-xl text-slate-600 dark:text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto">
                        A beautiful, lightning-fast way to navigate the latest scientific breakthroughs.
                        No ads. No login walls. Just pure science.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/app"
                            className="group bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-4 rounded-2xl font-semibold transition-all shadow-xl shadow-indigo-200 dark:shadow-none flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
                        >
                            Start Exploring
                            <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <a
                            href="#features"
                            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 text-lg px-8 py-4 rounded-2xl font-semibold hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all flex items-center justify-center"
                        >
                            Learn More
                        </a>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section id="features" className="py-24 bg-slate-50 dark:bg-slate-900/50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        <div className="p-8 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/50 rounded-2xl flex items-center justify-center text-indigo-600 dark:text-indigo-400 mb-6">
                                <Search size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Smart Discovery</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                Real-time paper feeds filtered by sophisticated topics like ML, CRISPR, and Quantum Computing.
                            </p>
                        </div>

                        <div className="p-8 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/50 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-6">
                                <Bookmark size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Personal Library</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                One-click bookmarking to save papers for later. Your library is stored locally and stays private.
                            </p>
                        </div>

                        <div className="p-8 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
                            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-2xl flex items-center justify-center text-purple-600 dark:text-purple-400 mb-6">
                                <Moon size={24} />
                            </div>
                            <h3 className="text-xl font-bold mb-3">Reader-First Design</h3>
                            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                                A clean, distraction-free interface with full dark mode support for late-night research sessions.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Powered By Section */}
            <section className="py-20">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-2xl font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest mb-8">
                        Built on Open Science
                    </h2>
                    <p className="text-2xl text-slate-700 dark:text-slate-300 font-medium italic">
                        "Scholar search is stuck in 2005. So we built something better."
                    </p>
                    <div className="mt-12 flex flex-wrap justify-center gap-8 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                        <span className="text-2xl font-bold">OpenAlex API</span>
                        <span className="text-2xl font-bold">React 19</span>
                        <span className="text-2xl font-bold">Bun Runtime</span>
                        <span className="text-2xl font-bold">Tailwind CSS</span>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-slate-100 dark:border-slate-900">
                <div className="max-w-7xl mx-auto px-4 text-center text-slate-500 dark:text-slate-400">
                    <p>© {new Date().getFullYear()} Eolas Online. Data provided by OpenAlex.</p>
                </div>
            </footer>
        </div>
    );
}
